import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactPlayer from 'react-player';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { 
  ArrowLeft, Play, CheckCircle, Clock, BookOpen, 
  ChevronRight, ChevronDown, Menu, X 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoursePlayerPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [progress, setProgress] = useState(0);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [courseRes, progressRes] = await Promise.all([
          axios.get(`${API}/products/${productId}`),
          axios.get(`${API}/progress/${productId}`, {
            headers: getAuthHeaders(),
            withCredentials: true
          })
        ]);
        
        setCourse(courseRes.data);
        setProgress(progressRes.data.progress_percentage || 0);
        
        // Set first lesson as current
        if (courseRes.data.lessons?.length > 0) {
          setCurrentLesson(courseRes.data.lessons[0]);
        }
      } catch (error) {
        console.error('Failed to fetch course:', error);
        toast.error('Failed to load course');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productId, getAuthHeaders, navigate]);

  const handleLessonComplete = async () => {
    if (!currentLesson || completedLessons.includes(currentLesson.id)) return;

    const newCompleted = [...completedLessons, currentLesson.id];
    setCompletedLessons(newCompleted);

    const newProgress = Math.round((newCompleted.length / (course?.lessons?.length || 1)) * 100);
    setProgress(newProgress);

    try {
      await axios.post(`${API}/progress`, {
        product_id: productId,
        lesson_id: currentLesson.id,
        progress_percentage: newProgress
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Progress saved!');
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  const handleVideoProgress = (state) => {
    // Mark as complete when video is 90% watched
    if (state.played > 0.9 && !completedLessons.includes(currentLesson?.id)) {
      handleLessonComplete();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A202C] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A202C] text-white">
      {/* Top Bar */}
      <div className="sticky top-0 z-50 bg-[#1A202C]/95 backdrop-blur border-b border-white/10">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-4">
            <Link 
              to="/dashboard" 
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Dashboard</span>
            </Link>
          </div>
          
          <div className="flex-1 max-w-lg mx-4 hidden md:block">
            <div className="flex items-center gap-3">
              <Progress value={progress} className="h-2 flex-1" />
              <span className="text-sm text-white/70">{progress}%</span>
            </div>
          </div>

          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 hover:bg-white/10 rounded-lg"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Main Content */}
        <div className={`flex-1 ${sidebarOpen ? 'lg:mr-80' : ''}`}>
          {/* Video Player */}
          <div className="aspect-video bg-black">
            {course?.video_url ? (
              <ReactPlayer
                url={course.video_url}
                width="100%"
                height="100%"
                controls
                playing={false}
                onProgress={handleVideoProgress}
                config={{
                  file: {
                    attributes: {
                      controlsList: 'nodownload'
                    }
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <Play className="w-16 h-16 mx-auto text-white/30 mb-4" />
                  <p className="text-white/50">No video available for this lesson</p>
                </div>
              </div>
            )}
          </div>

          {/* Lesson Info */}
          <div className="p-6 lg:p-8">
            <div className="flex items-center gap-2 text-white/50 text-sm mb-4">
              <BookOpen className="w-4 h-4" />
              {course?.title}
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold mb-4">
              {currentLesson?.title || 'Welcome to the Course'}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              {currentLesson?.duration && (
                <span className="flex items-center gap-2 text-white/70">
                  <Clock className="w-4 h-4" />
                  {currentLesson.duration}
                </span>
              )}
              {completedLessons.includes(currentLesson?.id) && (
                <span className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  Completed
                </span>
              )}
            </div>

            {!completedLessons.includes(currentLesson?.id) && (
              <Button 
                onClick={handleLessonComplete}
                className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                data-testid="mark-complete-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Mark as Complete
              </Button>
            )}

            {/* Lesson Description */}
            <div className="mt-8 prose prose-invert max-w-none">
              <p className="text-white/70">
                {course?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Sidebar - Course Content */}
        <div className={`fixed right-0 top-16 bottom-0 w-80 bg-[#0f1419] border-l border-white/10 overflow-y-auto transform transition-transform ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        } lg:translate-x-0`}>
          <div className="p-4">
            <h2 className="font-semibold text-lg mb-4">Course Content</h2>
            <div className="space-y-2">
              {course?.lessons?.map((lesson, index) => {
                const isActive = currentLesson?.id === lesson.id;
                const isCompleted = completedLessons.includes(lesson.id);
                
                return (
                  <button
                    key={lesson.id}
                    onClick={() => setCurrentLesson(lesson)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-[#2D4A5E] text-white' 
                        : 'hover:bg-white/5 text-white/70'
                    }`}
                    data-testid={`lesson-${lesson.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isCompleted 
                          ? 'bg-green-500 text-white' 
                          : isActive 
                            ? 'bg-white/20' 
                            : 'bg-white/10'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <span className="text-xs">{index + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium ${isActive ? 'text-white' : 'text-white/90'}`}>
                          {lesson.title}
                        </div>
                        {lesson.duration && (
                          <div className="text-sm text-white/50 mt-1">{lesson.duration}</div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoursePlayerPage;
